import { ClinicalEventEntry } from './ClinicalEventEntry';
import { gaDays } from './calculations';
import { formatInstant } from '@/lib/utils/date';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { evaluateBirthRules, type BirthRuleContext, type BirthTimer } from './birthRules';
import { activeBirthEvents } from './birthCalculations';
import type { BirthWorkspaceProps } from './LaborFlowsheet';
export interface BirthTimerAction {
  timer_key: string;
  action: 'ACKNOWLEDGE' | 'SNOOZE';
  at: string;
  until?: string;
  author_id: string;
  reason?: string;
}
export function birthTimerKey(timer: BirthTimer) {
  return `${timer.id}:${timer.anchor_event_id}:${timer.due_at_ms}`;
}
export function BirthMonitoring({
  episode,
  events,
  configuration,
  onEdit,
  timerActions = [],
  onTimerAction,
  onSave,
  disabled,
  edd,
  timeZone,
  encounterId,
}: BirthWorkspaceProps) {
  const [now, setNow] = useState(() => new Date().toISOString()),
    [minutes, setMinutes] = useState(''),
    [message, setMessage] = useState(''),
    [reason, setReason] = useState(''),
    [entryTimer, setEntryTimer] = useState<BirthTimer>();
  useEffect(() => {
    const update = () => setNow(new Date().toISOString());
    const interval = setInterval(update, 15000);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);
  const config = configuration?.birth_rules;
  if (!configuration?.clinically_reviewed || !config?.clinically_reviewed)
    return (
      <p className="text-sm text-muted-foreground">
        Labor and newborn alerts require a published, reviewed practice protocol.
      </p>
    );
  const confirmed = activeBirthEvents(events, episode.id, now) as import('./types').ClinicalEvent[];
  const active = confirmed.filter((e) => e.encounter_id === encounterId),
    motherId = episode.subjects.find((s) => s.subject_type === 'MOTHER')?.id ?? '';
  const newborns = episode.subjects
    .filter((s) => s.subject_type === 'NEWBORN' && s.active !== false)
    .flatMap((s) => {
      const birth = active.find(
        (e) => e.subject_id === s.fetus_subject_id && e.event_type === 'BIRTH' && e.payload.outcome === 'Live birth',
      );
      return birth
        ? [{ subject_id: s.id, fetus_id: s.fetus_subject_id!, birth_at: birth.occurred_at, birth_event_id: birth.id }]
        : [];
    });
  const profile = confirmed
    .filter((e) => e.event_type === 'PRENATAL.CONTEXT' && e.subject_id === motherId)
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0]?.payload;
  const gbs = String(profile?.gbs_status ?? '').toUpperCase();
  const ga = gaDays(formatInstant(now, 'yyyy-MM-dd', timeZone), edd);
  const admission = active
    .filter((e) => e.subject_id === motherId && e.event_type === 'NOTE.ADMISSION')
    .at(-1)?.payload;
  const fetusIds = episode.subjects.filter((s) => s.subject_type === 'FETUS' && s.active !== false).map((s) => s.id);
  const context = {
    episode_id: episode.id,
    mother_id: motherId,
    fetus_ids: fetusIds,
    labor_complete:
      fetusIds.length > 0 &&
      fetusIds.every((id) => active.some((e) => e.event_type === 'BIRTH' && e.subject_id === id)),
    newborns,
    now,
    ...(typeof admission?.parity === 'number' ? { parity: admission.parity } : {}),
    ...(['ACTIVE', 'PHYSIOLOGIC'].includes(String(admission?.third_stage_management))
      ? { third_stage_management: admission!.third_stage_management }
      : {}),
    ...(ga !== null ? { ga_days: ga } : {}),
    ...(['POSITIVE', 'NEGATIVE', 'UNKNOWN'].includes(gbs) ? { gbs_status: gbs } : {}),
  };
  const results = (['INTRAPARTUM', 'PP_MATERNAL', 'NEWBORN'] as const).map((stage) =>
    evaluateBirthRules(active, { ...context, stage } as BirthRuleContext, config),
  );
  const alerts = [...new Map(results.flatMap((r) => r.alerts).map((a) => [a.id, a])).values()];
  const timers = [...new Map(results.flatMap((r) => r.timers).map((t) => [birthTimerKey(t), t])).values()];
  async function act(timer: BirthTimer, action: 'ACKNOWLEDGE' | 'SNOOZE') {
    try {
      if (!onTimerAction) throw new Error('Unlock encrypted storage first.');
      const duration = Number(minutes);
      if (action === 'SNOOZE' && !reason.trim()) throw new Error('Enter a reason for snoozing.');
      if (action === 'SNOOZE' && (!Number.isFinite(duration) || duration <= 0 || duration > 240))
        throw new Error('Enter a snooze interval from 1 to 240 minutes.');
      await onTimerAction({
        timer_key: birthTimerKey(timer),
        action,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        at: new Date().toISOString(),
        ...(action === 'SNOOZE' ? { until: new Date(Date.now() + duration * 60000).toISOString() } : {}),
      });
      setMessage('Saved in this device’s encrypted timer history.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Unable to save timer action.');
    }
  }
  return (
    <section aria-label="Birth monitoring" className="flex flex-col gap-3 rounded border p-4">
      <h3 className="font-semibold">Practice protocol reminders</h3>
      <p className="text-sm text-muted-foreground">
        Calculated from confirmed observations. Pending local observations are listed separately. Browser reminders are
        unavailable while the app is suspended.
      </p>
      {results.flatMap((r) => r.errors).length > 0 && (
        <p role="alert">The stored protocol is invalid; review its configuration.</p>
      )}
      {alerts.map((a) => (
        <div key={a.id} className="rounded border p-3">
          <p>
            {a.severity}: {a.message} · {episode.subjects.find((s) => s.id === a.subject_id)?.label}
          </p>
          <p className="text-sm text-muted-foreground">{a.citation}</p>
          {onEdit &&
            a.source_event_ids.map((id) => {
              const e = events.find((e) => e.id === id);
              return e ? (
                <Button type="button" variant="ghost" className="min-h-11" key={id} onClick={() => onEdit(e)}>
                  Review {e.event_type}
                </Button>
              ) : null;
            })}
        </div>
      ))}
      <Field className="max-w-56">
        <FieldLabel htmlFor="birth-timer-snooze">Snooze interval (minutes)</FieldLabel>
        <Input
          id="birth-timer-snooze"
          className="min-h-11"
          type="number"
          min={1}
          max={240}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="birth-timer-reason">Snooze reason</FieldLabel>
        <Input
          id="birth-timer-reason"
          className="min-h-11"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      {timers.map((timer) => {
        const action = timerActions.filter((a) => a.timer_key === birthTimerKey(timer)).at(-1);
        return (
          <div
            key={birthTimerKey(timer)}
            className={`flex flex-wrap items-center gap-3 rounded border p-3 ${timer.state === 'overdue' ? 'border-destructive bg-destructive/5' : timer.state === 'due_soon' ? 'border-primary bg-accent' : ''}`}>
            <p>
              {timer.label} · {timer.state} · due {new Date(timer.due_at_ms).toLocaleTimeString()}{' '}
              {action?.action === 'SNOOZE' && action.until && Date.parse(action.until) > Date.parse(now)
                ? `· Snoozed locally until ${new Date(action.until).toLocaleTimeString()}`
                : action?.action === 'ACKNOWLEDGE'
                  ? '· Acknowledged locally'
                  : ''}
            </p>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={disabled || !onTimerAction}
              onClick={() => act(timer, 'ACKNOWLEDGE')}>
              Acknowledge
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={disabled || !onTimerAction}
              onClick={() => act(timer, 'SNOOZE')}>
              Snooze
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={disabled}
              onClick={() => setEntryTimer(timer)}>
              Enter due observations
            </Button>
          </div>
        );
      })}
      {entryTimer && (
        <ClinicalEventEntry
          key={birthTimerKey(entryTimer)}
          descriptors={(config.timers.find((t) => t.id === entryTimer.protocol_id)?.observations ?? []).map(
            (eventType) => ({ eventType, label: eventType }),
          )}
          subjects={episode.subjects.filter((s) => s.id === entryTimer.subject_id)}
          events={events}
          onSave={onSave}
          timeZone={timeZone}
          disabled={disabled}
          title={`Document ${entryTimer.label}`}
          onSaved={() => setEntryTimer(undefined)}
        />
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
